import { Map } from 'immutable'
import _toInteger from 'lodash/toInteger'
import _toNumber from 'lodash/toNumber'
import _omitBy from 'lodash/omitBy'
import _isUndefined from 'lodash/isUndefined'
import _isString from 'lodash/isString'
import { getBaseContentType } from '../content-type/index'

/**
 * Wire event names that dual-produce to Brooklyn eventLog (when auth JWT present).
 */
export const AI_EVENT_LOG_EVENTS = [
  'ai_cancel_modal_cancel_clicked',
  'ai_cancel_modal_engaged',
  'ai_cancel_modal_viewed',
  'ai_conversation_opened',
  'ai_conversation_deleted',
  'ai_conversation_favorited',
  'ai_conversation_name_updated',
  'ai_conversation_resumed',
  'ai_conversation_started',
  'ai_conversation_unfavorited',
  'ai_discover_viewed',
  'ai_discover_playlist_followed',
  'ai_discover_video_clicked',
  'ai_discover_refined',
  'ai_discover_search_completed',
  'ai_experience_entered',
  'ai_experience_exited',
  'ai_enriched_document_received',
  'ai_error_response',
  'ai_message_feedback',
  'ai_message_failure',
  'ai_message_received',
  'ai_message_regenerated',
  'ai_message_sent',
  'ai_module_clicked',
  'ai_module_closed',
  'ai_module_opened',
  'ai_module_viewed',
  'ai_moderation_violation',
  'ai_navigation_clicked',
  'ai_page_view_chat_home',
  'ai_persona_selected',
  'ai_rate_limited',
  'ai_recommendation_clicked',
  'ai_recommendation_viewed',
  'ai_recommendation_received',
  'ai_speech_to_text',
  'ai_unrecognized_input',
  'ai_videoguide_disabled',
  'ai_videoguide_enabled',
  'concept_moment_toast_shown',
  'concept_moment_toast_clicked',
  'concept_moment_toast_dismissed',
]

const AI_EVENT_NAME_MAP = {
  ai_cancel_modal_cancel_clicked: 'AiCancelModalCancelClicked',
  ai_cancel_modal_engaged: 'AiCancelModalEngaged',
  ai_cancel_modal_viewed: 'AiCancelModalViewed',
  ai_conversation_opened: 'AiConversationOpened',
  ai_conversation_deleted: 'AiConversationDeleted',
  ai_conversation_favorited: 'AiConversationFavorited',
  ai_conversation_name_updated: 'AiConversationNameUpdated',
  ai_conversation_resumed: 'AiConversationResumed',
  ai_conversation_started: 'AiConversationStarted',
  ai_conversation_unfavorited: 'AiConversationUnfavorited',
  ai_discover_viewed: 'AiDiscoverViewed',
  ai_discover_playlist_followed: 'AiDiscoverPlaylistFollowed',
  ai_discover_video_clicked: 'AiDiscoverVideoClicked',
  ai_discover_refined: 'AiDiscoverRefined',
  ai_discover_search_completed: 'AiDiscoverSearchCompleted',
  ai_experience_entered: 'AiExperienceEntered',
  ai_experience_exited: 'AiExperienceExited',
  ai_enriched_document_received: 'AiEnrichedDocumentReceived',
  ai_error_response: 'AiErrorResponse',
  ai_message_feedback: 'AiMessageFeedback',
  ai_message_failure: 'AiMessageFailure',
  ai_message_received: 'AiMessageReceived',
  ai_message_regenerated: 'AiMessageRegenerated',
  ai_message_sent: 'AiMessageSent',
  ai_module_clicked: 'AiModuleClicked',
  ai_module_closed: 'AiModuleClosed',
  ai_module_opened: 'AiModuleOpened',
  ai_module_viewed: 'AiModuleViewed',
  ai_moderation_violation: 'AiModerationViolation',
  ai_navigation_clicked: 'AiNavigationClicked',
  ai_page_view_chat_home: 'AiPageViewChatHome',
  ai_persona_selected: 'AiPersonaSelected',
  ai_rate_limited: 'AiRateLimited',
  ai_recommendation_clicked: 'AiRecommendationClicked',
  ai_recommendation_viewed: 'AiRecommendationViewed',
  ai_recommendation_received: 'AiRecommendationReceived',
  ai_speech_to_text: 'AiSpeechToText',
  ai_unrecognized_input: 'AiUnrecognizedInput',
  ai_videoguide_disabled: 'AiVideoguideDisabled',
  ai_videoguide_enabled: 'AiVideoguideEnabled',
  concept_moment_toast_shown: 'ConceptMomentToastShown',
  concept_moment_toast_clicked: 'ConceptMomentToastClicked',
  concept_moment_toast_dismissed: 'ConceptMomentToastDismissed',
}

function formatAiGaiaEventName (eventName) {
  if (!eventName || !_isString(eventName)) {
    return undefined
  }
  return AI_EVENT_NAME_MAP[eventName]
}

function serializeJsonField (value) {
  if (value === undefined || value === null) {
    return undefined
  }
  if (typeof value === 'string') {
    return value
  }
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

function mapContentRatingToRating (contentRating) {
  if (contentRating === 'up') return 1
  if (contentRating === 'down') return 0
  return undefined
}

/**
 * Format enriched AI GA4 payload for Brooklyn eventLog mutation.
 * Returns {} when aiSessionId is missing (skip mutation), except for
 * ai_conversation_started, which is inherently pre-session (AI-1396).
 * @param {Map} data - Enriched Immutable event from pushAiGa4Event
 * @returns {Object} EventLogInput-shaped object
 */
export function formatAiEventLogData (data = Map()) {
  if (!Map.isMap(data) || data.isEmpty()) {
    return {}
  }

  const rawEventName = data.get('event') || data.get('eventName')
  const formattedEventName = formatAiGaiaEventName(rawEventName)
  if (!formattedEventName) {
    return {}
  }

  // AI-1396: ai_conversation_started fires before the backend session id exists
  // (the session is only minted mid-stream on the first send), so requiring one
  // here silently dropped the event from the eventLog→Braze pipeline on every
  // conversation start — the GA4 tracking plan marks it "Add to Braze".
  // Brooklyn's eventLog rejects a missing aiSessionId but accepts an empty one, so
  // it goes out as ''. Every other event keeps the guard because their payloads
  // are meaningless without a session.
  const aiSessionId = data.get('chat_session_id') || data.get('ai_session_id')
  const hasSessionId = Boolean(aiSessionId) && String(aiSessionId).trim() !== ''
  if (!hasSessionId && rawEventName !== 'ai_conversation_started') {
    return {}
  }

  const contentIdRaw = data.get('content_id')
  const numericContentId = _toNumber(contentIdRaw)
  const hasValidContentId = numericContentId >= 1
  const contentType = hasValidContentId ? getBaseContentType(data.get('content_type')) : undefined

  const tileNumberRaw = data.has('tile_number')
    ? data.get('tile_number')
    : data.get('position')
  const videoTimeRaw = data.get('video_time_seconds')

  const contentRating = data.get('content_rating')
  const ratingFromFeedback = rawEventName === 'ai_message_feedback'
    ? mapContentRatingToRating(contentRating)
    : undefined

  const eventData = _omitBy({
    aiSessionId: hasSessionId ? String(aiSessionId) : '',
    aiPersona: data.get('ai_persona'),
    conversationTitle: data.get('conversation_title'),
    aiChatExperienceName: data.get('chat_experience_name'),
    aiContext: data.get('context'),
    inputMethod: data.get('input_method'),
    messageLength: data.has('message_length') ? _toInteger(data.get('message_length')) : undefined,
    uiElement: data.get('ui_element'),
    uiLocation: data.get('ui_location'),
    aiMessagePairId: data.get('message_pair_id'),
    aiMessageSubmissionMethod: data.get('submission_method'),
    aiModuleOpenIsInitial: data.get('is_initial'),
    aiModuleCloseMethod: data.get('method'),
    aiSessionDurationMs: data.has('session_duration_ms')
      ? _toInteger(data.get('session_duration_ms'))
      : undefined,
    aiErrorDetails: data.get('error_details'),
    aiPlayingContentId: data.get('playing_content_id') != null
      ? String(data.get('playing_content_id'))
      : undefined,
    aiPlayingFeatureMediaId: data.get('playing_feature_media_id') != null
      ? String(data.get('playing_feature_media_id'))
      : undefined,
    position: videoTimeRaw != null ? _toInteger(videoTimeRaw) : undefined,
    rating: ratingFromFeedback,
    aiEnrichedDocumentType: data.get('enriched_document_type'),
    aiEnrichedDocumentInfo: serializeJsonField(data.get('enriched_document_info')),
    aiModerationSeverity: data.get('severity'),
    aiModerationCategories: serializeJsonField(data.get('categories')),
    aiDiscoverResultCount: data.has('result_count') ? _toInteger(data.get('result_count')) : undefined,
    aiDiscoverHadResults: data.get('had_results'),
    aiDiscoverSearchState: data.get('state'),
    aiDiscoverRefinementLength: data.has('refinement_length')
      ? _toInteger(data.get('refinement_length'))
      : undefined,
    aiDiscoverRefinementWordCount: data.has('refinement_word_count')
      ? _toInteger(data.get('refinement_word_count'))
      : undefined,
    aiDiscoverPlaylistId: data.get('playlist_id'),
    tileNumber: tileNumberRaw != null ? _toInteger(tileNumberRaw) : undefined,
    aiConceptMomentStartSeconds: data.has('moment_start_seconds')
      ? _toInteger(data.get('moment_start_seconds'))
      : undefined,
    aiConceptMomentLabel: data.get('concept_label'),
    ...(hasValidContentId ? {
      contentId: numericContentId,
      contentType,
    } : {}),
    contentName: data.get('content_name') || data.get('content_title'),
    parentSeriesId: data.get('parent_series_id'),
    parentSeriesName: data.get('parent_series_name'),
    mediaId: data.get('media_id') != null ? String(data.get('media_id')) : undefined,
    mediaLanguage: data.get('media_language'),
    isFeaturedMedia: data.get('is_featured_media'),
    membershipStatus: data.get('membership_status'),
    userInTest: data.get('user_in_test'),
    userLanguage: data.get('user_language'),
    guestPhase: data.get('guest_phase'),
    appName: data.get('app_name'),
    appVersion: data.get('app_version'),
    allTestsCollated: data.get('all_tests_collated'),
    pageLocation: data.get('current_url'),
    externalId: data.get('gaia_uuid'),
    eventNumber: data.has('event_number') ? _toInteger(data.get('event_number')) : undefined,
  }, _isUndefined)

  return {
    eventName: formattedEventName,
    data: eventData,
  }
}
